<script setup>
import { computed, onMounted, ref } from 'vue';
import { globalState, getUser, getUserAsync, refreshUsage, setToken } from '@/lib/user';
import { claimDailyCredits, getDailyCreditsStatus } from '@/lib/api';
import { useToast } from '@/components/ui/toast';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useRouter } from 'vue-router';
import { Loader2 } from 'lucide-vue-next';
import { $t } from '@/i18n';

const user = computed(() => globalState.user);

const router = useRouter();
const { toast } = useToast();
const dailyClaimed = ref(false);
const claimLoading = ref(false);

async function checkDailyStatus() {
  try {
    const status = await getDailyCreditsStatus();
    dailyClaimed.value = !status.canClaim;
  } catch (error) {
    console.error('Failed to check daily status:', error);
  }
}

async function handleClaim(e) {
  e.preventDefault();
  if (dailyClaimed.value || claimLoading.value) return;

  claimLoading.value = true;
  try {
    await claimDailyCredits();
    dailyClaimed.value = true;
    toast({
      title: $t('dailyCredits.success.title'),
      description: $t('dailyCredits.success.description'),
      variant: 'success', // Assuming success variant exists or default is fine
    });
    // Refresh user credits
    getUser();
    refreshUsage();
  } catch (error) {
    console.error('Failed to claim credits:', error);
    toast({
      title: $t('dailyCredits.error.title'),
      description: error.message || $t('dailyCredits.error.description'),
      variant: 'destructive',
    });
  } finally {
    claimLoading.value = false;
  }
}

onMounted(() => {
  getUser();
  getUserAsync();
  checkDailyStatus();
  refreshUsage().catch((e) => {
    if (e.code === 'TOKEN_EXPIRED') {
      setToken('');
      router.push({ name: 'SignIn' });
    }
  });
});

function logout() {
  setToken('');
  getUser();
}
</script>
<template>
  <div class="flex gap-1 items-center">
    <!-- 用户头像下拉菜单 -->
    <DropdownMenu>
      <DropdownMenuTrigger class="focus:outline-none">
        <div
          class="flex items-center gap-1 p-1 h-9 rounded-full hover:bg-black/5 transition-all duration-200 cursor-pointer group relative"
        >
          <!-- 
            用户头像样式说明：
        
          -->
          <Avatar class="h-7 w-7 rounded-full bg-transparent transition-all duration-300 ease-out">
            <AvatarImage
              :src="user.avatar || ''"
              :alt="user.name"
              class="rounded-full object-contain"
            />
            <!-- 
              头像占位符样式（无头像时显示）：
            
            -->
            <AvatarFallback
              class="w-full h-full flex items-center justify-center rounded-full bg-[#3C31FF] text-white font-normal text-sm"
            >
              {{ user.name?.slice(0, 1) || 'A' }}
            </AvatarFallback>
          </Avatar>
        </div>
      </DropdownMenuTrigger>

      <!-- 
      -->
      <DropdownMenuContent
        class="w-72 bg-white/80 backdrop-blur-[60px] rounded-[20px] shadow-[0_8px_32px_rgba(0,0,0,0.05)] border border-black/10 p-2 mt-1"
        align="end"
      >
        <!-- 

        -->
        <DropdownMenuLabel class="font-light px-3 py-6">
          <div class="flex flex-col items-center gap-4">
            <Avatar class="h-[60px] w-[60px] rounded-full bg-transparent">
              <AvatarImage :src="user.avatar || ''" :alt="user.name" class="rounded-full" />

              <AvatarFallback
                class="rounded-full bg-[#3C31FF] text-white font-normal text-xl w-full h-full flex items-center justify-center"
              >
                {{ user.name?.slice(0, 1) || 'A' }}
              </AvatarFallback>
            </Avatar>

            <!-- 用户信息文字 -->
            <div class="flex flex-col gap-0.5 items-center">
              <!-- 用户名 -->
              <span class="text-sm font-normal text-black/80">
                {{ user.name }}
              </span>
              <!-- 邮箱地址 -->
              <span class="text-sm font-normal text-black/60">{{ user.email }}</span>
            </div>
          </div>
        </DropdownMenuLabel>

        <!-- 分隔线 -->
        <DropdownMenuSeparator class="bg-black/5 my-2" />

        <!-- 菜单项列表 -->
        <div class="space-y-0.5">
          <DropdownMenuItem
            class="rounded-[12px] px-1 py-1 hover:bg-black/5 transition-all duration-200 cursor-pointer focus:bg-black/5"
          >
            <router-link :to="`/workspace`" class="flex w-full items-center gap-2 text-black/80">
              <div class="flex items-center justify-center w-8 h-8 rounded-[10px]">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M2.33105 10.1758C1.96191 10.1758 1.74316 9.90918 1.74316 9.62891C1.74316 9.47168 1.81152 9.30762 1.96191 9.18457L9.08496 3.20312C9.34473 2.98438 9.625 2.875 9.90527 2.875C10.1855 2.875 10.4658 2.98438 10.7256 3.20312L14.3555 6.24512V4.91211C14.3555 4.61133 14.5537 4.41992 14.8477 4.41992H15.5791C15.8799 4.41992 16.0713 4.61133 16.0713 4.91211V7.6875L17.8486 9.18457C18.0059 9.30762 18.0674 9.47168 18.0674 9.62891C18.0674 9.90918 17.8486 10.1758 17.4795 10.1758C17.2881 10.1758 17.1309 10.0732 16.9941 9.95703L16.0713 9.17773V15.7402C16.0713 16.6973 15.5039 17.251 14.5332 17.251H5.28418C4.30664 17.251 3.73926 16.7041 3.73926 15.7402V9.17773L2.82324 9.95703C2.67969 10.0732 2.52246 10.1758 2.33105 10.1758ZM11.8877 12.1309V16.1504H14.3008C14.7314 16.1504 14.9707 15.9111 14.9707 15.4668V8.25488L10.1445 4.20801C10.0693 4.14648 9.9873 4.1123 9.90527 4.1123C9.83008 4.1123 9.74121 4.14648 9.66602 4.20801L4.83984 8.25488V15.4668C4.83984 15.9111 5.0791 16.1504 5.5166 16.1504H7.92285V12.1309C7.92285 11.8164 8.13477 11.6113 8.44922 11.6113H11.3682C11.6826 11.6113 11.8877 11.8164 11.8877 12.1309Z"
                    fill="currentColor"
                  />
                </svg>
              </div>
              <span class="font-normal text-black/80 text-sm sidebar-link-label">
                {{ $t('app.Workspace') }}
              </span>
            </router-link>
          </DropdownMenuItem>

          <!-- 每日签到菜单项 -->
          <DropdownMenuItem
            class="rounded-[12px] px-1 py-1 hover:bg-black/5 transition-all duration-200 cursor-pointer focus:bg-black/5"
            @select="(e) => e.preventDefault()"
            @click="handleClaim"
          >
            <div class="flex w-full items-center gap-2 text-black/80">
              <div class="flex items-center justify-center w-8 h-8 rounded-[10px]">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M10 2.91406C10 7.80926 12.1429 9.98513 17.0711 9.98513C12.1429 9.98483 10 12.1286 10 17.0562C10 12.1286 7.85714 9.98513 2.92893 9.98513C7.85714 9.98513 10 7.84291 10 2.91406Z"
                    stroke="black"
                  />
                </svg>
              </div>
              <span class="font-normal text-black/80 text-sm sidebar-link-label flex-1">
                {{ dailyClaimed ? $t('app.claimed') : $t('app.claimFreeCredits') }}
              </span>
              <div v-if="claimLoading" class="mr-2">
                <Loader2 class="animate-spin h-4 w-4 text-black/40" />
              </div>
              <span v-else-if="dailyClaimed" class="text-xs font-medium text-emerald-600 mr-1">
                +5
              </span>
            </div>
          </DropdownMenuItem>

          <!-- 邀请好友菜单项 -->
          <DropdownMenuItem
            class="rounded-[12px] px-1 py-1 hover:bg-black/5 transition-all duration-200 cursor-pointer focus:bg-black/5"
          >
            <router-link to="/invite" class="flex w-full items-center gap-2 text-black/80">
              <div class="flex items-center justify-center w-8 h-8 rounded-[10px]">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M9.72962 12.8265C8.85462 12.6761 8.30774 12.3685 7.87708 11.9379C6.21594 10.2836 6.22962 7.95935 7.87024 6.31873L10.2013 3.98084C11.8419 2.34021 14.1661 2.32654 15.8273 3.98767C17.4816 5.64881 17.4679 7.96619 15.8341 9.60681L13.8243 11.6097C13.8927 11.1654 13.8517 10.6595 13.6603 10.222L15.0001 8.8822C16.1827 7.69959 16.1964 6.01111 15.0001 4.81482C13.8038 3.61853 12.1222 3.62537 10.9259 4.81482L8.69739 7.04334C7.51477 8.22595 7.50794 9.92127 8.70423 11.1176C9.14856 11.5619 9.73645 11.8353 10.6388 11.9037L9.72962 12.8265ZM9.90051 7.19373C10.7755 7.34412 11.3224 7.65173 11.7531 8.0824C13.4142 9.7367 13.4005 12.0609 11.7599 13.7015L9.422 16.0463C7.78821 17.6801 5.46399 17.6937 3.80286 16.0326C2.14856 14.3783 2.16223 12.0541 3.79602 10.4135L5.80579 8.41052C5.73059 8.85486 5.77844 9.36756 5.96985 9.79822L4.63001 11.1381C3.44055 12.3275 3.43372 14.0092 4.63001 15.2123C5.8263 16.4017 7.50794 16.3949 8.69739 15.2054L10.9327 12.9769C12.1154 11.7943 12.1222 10.099 10.9259 8.90271C10.4816 8.45838 9.89368 8.19177 8.99133 8.11658L9.90051 7.19373Z"
                    fill="black"
                  />
                </svg>
              </div>
              <span class="font-normal text-black/80 text-sm sidebar-link-label">
                {{ $t('app.invite') }}
              </span>
            </router-link>
          </DropdownMenuItem>

          <!-- 兑换码菜单项 -->
          <DropdownMenuItem
            class="rounded-[12px] px-1 py-1 hover:bg-black/5 transition-all duration-200 cursor-pointer focus:bg-black/5"
          >
            <router-link
              :to="{ name: 'Redeem' }"
              class="flex w-full items-center gap-2 text-black/80"
            >
              <div class="flex items-center justify-center w-8 h-8 rounded-[10px]">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M6.72363 16.8438C5.43164 16.8438 4.7207 16.167 4.7207 14.875V10.6777H4.67285C4.07129 10.5479 3.74316 10.0625 3.74316 9.36523V7.56738C3.74316 6.7334 4.24219 6.20703 5.04199 6.20703H6.42285C6.05371 5.84473 5.8418 5.35254 5.8418 4.78516C5.8418 3.45898 6.89453 2.51562 8.21387 2.51562C9.17773 2.51562 9.96387 3.04199 10.3125 3.95801C10.6611 3.04199 11.4541 2.51562 12.4111 2.51562C13.7373 2.51562 14.7832 3.45898 14.7832 4.78516C14.7832 5.35254 14.5713 5.84473 14.209 6.20703H15.5898C16.4238 6.20703 16.8887 6.7334 16.8887 7.56738V9.36523C16.8887 10.0625 16.5537 10.5479 15.9521 10.6777H15.9043V14.875C15.9043 16.167 15.1934 16.8438 13.9082 16.8438H6.72363ZM9.75195 6.20703V5.28418C9.75195 4.27246 9.0957 3.65723 8.28223 3.65723C7.53027 3.65723 6.9834 4.09473 6.9834 4.84668C6.9834 5.59863 7.6123 6.20703 8.68555 6.20703H9.75195ZM10.873 6.20703H11.9395C13.0195 6.20703 13.6416 5.59863 13.6416 4.84668C13.6416 4.09473 13.0947 3.65723 12.3428 3.65723C11.5293 3.65723 10.873 4.27246 10.873 5.28418V6.20703ZM9.7041 9.69336V7.23926H5.32227C4.98047 7.23926 4.84375 7.37598 4.84375 7.71777V9.20801C4.84375 9.5498 4.98047 9.69336 5.32227 9.69336H9.7041ZM10.9209 9.69336H15.3164C15.6582 9.69336 15.7881 9.5498 15.7881 9.20801V7.71777C15.7881 7.37598 15.6582 7.23926 15.3164 7.23926H10.9209V9.69336ZM9.7041 15.8184V10.7256H5.82129V14.9297C5.82129 15.4971 6.14258 15.8184 6.70996 15.8184H9.7041ZM10.9209 15.8184H13.915C14.4824 15.8184 14.8037 15.4971 14.8037 14.9297V10.7256H10.9209V15.8184Z"
                    fill="currentColor"
                  />
                </svg>
              </div>
              <span class="font-normal text-black/80 text-sm sidebar-link-label">
                {{ $t('app.RedemptionCode') }}
              </span>
            </router-link>
          </DropdownMenuItem>

          <!-- API Keys 菜单项 -->
          <DropdownMenuItem
            class="rounded-[12px] px-1 py-1 hover:bg-black/5 transition-all duration-200 cursor-pointer focus:bg-black/5"
          >
            <router-link
              :to="{ name: 'Workspace.settings' }"
              class="flex w-full items-center gap-2 text-black/80"
            >
              <div class="flex items-center justify-center w-8 h-8 rounded-[10px]">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M10.7773 16.6004C10.6133 16.6004 10.4492 16.5936 10.2783 16.5799L9.90918 17.284C9.81348 17.4617 9.66309 17.5438 9.46484 17.5096C9.28027 17.4686 9.15723 17.3455 9.12988 17.1473L9.02051 16.3611C8.69922 16.2723 8.38477 16.1629 8.08398 16.0193L7.48926 16.5526C7.35254 16.6893 7.1748 16.7098 6.99707 16.6072C6.83301 16.5115 6.76465 16.3475 6.80566 16.1561L6.96973 15.3836C6.70312 15.1854 6.44336 14.9734 6.2041 14.7342L5.47266 15.0418C5.29492 15.1102 5.12402 15.076 4.9873 14.9188C4.87109 14.7752 4.85059 14.5975 4.95996 14.4266L5.38379 13.7635C5.19238 13.4832 5.02832 13.1961 4.88477 12.8953L4.0918 12.9227C3.89355 12.9295 3.75 12.8406 3.6748 12.6492C3.62012 12.4647 3.66113 12.2938 3.81836 12.1776L4.44727 11.6922C4.36523 11.3709 4.30371 11.0428 4.27637 10.701L3.51758 10.4617C3.32617 10.4002 3.22363 10.2635 3.22363 10.0652C3.22363 9.867 3.32617 9.72345 3.51758 9.66876L4.27637 9.4295C4.30371 9.08771 4.36523 8.75958 4.44727 8.43829L3.81836 7.95294C3.66113 7.83673 3.60645 7.66583 3.68164 7.48126C3.75 7.29669 3.89355 7.19415 4.09863 7.20782L4.88477 7.23517C5.02832 6.94122 5.19238 6.64728 5.38379 6.37384L4.95996 5.70392C4.85059 5.53302 4.85742 5.35529 4.9873 5.21173C5.11719 5.06134 5.29492 5.01349 5.47266 5.08868L6.2041 5.38946C6.44336 5.15704 6.69629 4.94513 6.96973 4.75372L6.80566 3.97443C6.76465 3.78302 6.82617 3.61896 6.99707 3.52325C7.1748 3.42755 7.35254 3.44122 7.49609 3.57794L8.07715 4.11114C8.37793 3.97443 8.69238 3.85821 9.01367 3.76251L9.12988 2.97638C9.15723 2.78497 9.27344 2.65509 9.47168 2.62091C9.65625 2.59357 9.81348 2.66876 9.90918 2.8465L10.2783 3.54376C10.4492 3.53693 10.6133 3.53009 10.7773 3.53009C10.9482 3.53009 11.1123 3.53693 11.2764 3.54376L11.6523 2.83966C11.7412 2.66876 11.8984 2.58673 12.0967 2.62091C12.2812 2.66193 12.4043 2.78497 12.4248 2.97638L12.5342 3.76251C12.8623 3.85821 13.1768 3.96759 13.4707 4.10431L14.0654 3.57794C14.209 3.44806 14.3867 3.42071 14.5645 3.52325C14.7285 3.61896 14.7969 3.77618 14.7559 3.97443L14.585 4.74689C14.8516 4.93829 15.1113 5.15021 15.3506 5.38946L16.0889 5.08868C16.2666 5.02032 16.4375 5.0545 16.5742 5.20489C16.6904 5.35529 16.7041 5.53302 16.5947 5.70392L16.1777 6.367C16.3623 6.64044 16.5264 6.93439 16.6768 7.22833L17.4629 7.20782C17.6611 7.20099 17.8115 7.28986 17.8799 7.48126C17.9414 7.66583 17.9004 7.83673 17.7363 7.95294L17.1074 8.43829C17.1963 8.75958 17.251 9.08771 17.2852 9.4295L18.0371 9.66876C18.2285 9.72345 18.3379 9.867 18.3379 10.0652C18.3379 10.2635 18.2285 10.4002 18.0371 10.4617L17.2852 10.701C17.251 11.0428 17.1963 11.3709 17.1074 11.6922L17.7432 12.1776C17.9004 12.2938 17.9482 12.4647 17.8799 12.6424C17.8115 12.8338 17.6611 12.9295 17.4629 12.9227L16.6768 12.8953C16.5264 13.1893 16.3623 13.4764 16.1777 13.7567L16.6016 14.4266C16.7109 14.5975 16.6973 14.7684 16.5674 14.9188C16.4375 15.0692 16.2666 15.1102 16.082 15.0418L15.3506 14.741C15.1113 14.9666 14.8584 15.1854 14.585 15.3768L14.7559 16.1561C14.7969 16.3475 14.7285 16.5115 14.5576 16.6072C14.3867 16.6961 14.209 16.6893 14.0654 16.5526L13.4775 16.0193C13.1768 16.1561 12.8691 16.2723 12.541 16.3611L12.4248 17.1541C12.4043 17.3455 12.2881 17.4754 12.0898 17.5096C11.8984 17.5301 11.7412 17.4617 11.6523 17.284L11.2764 16.5799C11.1123 16.5936 10.9482 16.6004 10.7773 16.6004ZM10.7842 8.27423C11.6045 8.27423 12.3018 8.82794 12.5068 9.58673H16.1572C15.918 6.82501 13.6006 4.66486 10.7773 4.66486C9.9502 4.66486 9.16406 4.84943 8.45996 5.18439L10.2988 8.33575C10.4492 8.29474 10.6133 8.27423 10.7842 8.27423ZM5.37695 10.0652C5.37695 11.8563 6.24512 13.4422 7.58496 14.4266L9.49219 11.3094C9.18457 10.9881 8.99316 10.5438 8.99316 10.0652C8.99316 9.58673 9.17773 9.15607 9.47852 8.83478L7.62598 5.67657C6.26562 6.65411 5.37695 8.25372 5.37695 10.0652ZM10.7842 10.8309C11.208 10.8309 11.5498 10.4891 11.5498 10.0652C11.5498 9.64142 11.208 9.29962 10.7842 9.29962C10.3604 9.29962 10.0186 9.64142 10.0186 10.0652C10.0186 10.4891 10.3604 10.8309 10.7842 10.8309ZM10.7773 15.4656C13.6074 15.4656 15.9248 13.2986 16.1641 10.5301H12.5137C12.3086 11.2957 11.6113 11.8563 10.7842 11.8563C10.6201 11.8563 10.4629 11.8358 10.3125 11.7947L8.41211 14.9188C9.12988 15.2674 9.92969 15.4656 10.7773 15.4656Z"
                    fill="currentColor"
                  />
                </svg>
              </div>
              <span class="font-normal text-black/80 text-sm sidebar-link-label">
                {{ $t('app.accountSettings') }}
              </span>
            </router-link>
          </DropdownMenuItem>
        </div>

        <!-- 分隔线 -->
        <DropdownMenuSeparator class="bg-black/5 my-2" />

        <!-- 
          退出登录菜单项（特殊红色样式）：
          - text-red-600: 红色文字
          - hover:bg-red-50: 悬停时浅红色背景
          - hover:text-red-600: 悬停时保持红色文字
          - focus:bg-red-50: 聚焦时浅红色背景
          - focus:text-red-600: 聚焦时保持红色文字
        -->
        <DropdownMenuItem
          class="rounded-[12px] px-1 py-1 text-red-600 hover:bg-red-50 hover:text-red-600 transition-all duration-200 cursor-pointer focus:bg-red-50 focus:text-red-600"
          @click="logout"
        >
          <div class="flex items-center gap-2">
            <!-- 
              退出登录图标容器（红色主题）：
            -->
            <div class="flex items-center justify-center w-8 h-8 rounded-[10px]">
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M10 9.99609C9.67188 9.99609 9.45312 9.75684 9.45312 9.41504V3.27637C9.45312 2.92773 9.67188 2.69531 10 2.69531C10.3281 2.69531 10.5537 2.92773 10.5537 3.27637V9.41504C10.5537 9.75684 10.3281 9.99609 10 9.99609ZM10 17.0371C6.15137 17.0371 3.02734 13.9131 3.02734 10.0645C3.02734 7.96582 3.96387 6.11328 5.29688 4.94434C5.90527 4.4043 6.67773 5.2041 6.06934 5.77148C4.91406 6.83789 4.18945 8.34863 4.18945 10.0645C4.18945 13.2773 6.78711 15.875 10 15.875C13.2129 15.875 15.8105 13.2773 15.8105 10.0645C15.8105 8.3623 15.0928 6.84473 13.9307 5.77832C13.3291 5.21777 14.1084 4.38379 14.71 4.9375C16.0293 6.13379 16.9727 7.9043 16.9727 10.0645C16.9727 13.9131 13.8486 17.0371 10 17.0371Z"
                  fill="black"
                />
              </svg>
            </div>
            <span class="font-normal text-black/80 text-sm sidebar-link-label">
              {{ $t('app.logout') }}
            </span>
          </div>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  </div>
</template>
